<script setup lang="ts">
import Autolinker from 'autolinker';
import {
  BanIcon,
  CashIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardCheckIcon,
  ClipboardCopyIcon,
  CollectionIcon,
  CubeIcon,
  CubeTransparentIcon,
  DocumentTextIcon,
  ExclamationIcon,
  ExternalLinkIcon,
  MusicNoteIcon,
  PhotographIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  HashtagIcon,
  XIcon,
  VideoCameraIcon,
} from '@heroicons/vue/outline';
import { FireIcon } from '@heroicons/vue/solid';
import { computed } from 'vue';
import CollectionHandle from '~/components/CollectionHandle.vue';
import DetailsProvenance from '~/components/details/DetailsProvenance.vue';
import { getApiRoot } from '~/utilities/api';
import { BASE_TITLE } from '~/utilities/constants';

definePageMeta({
  backgroundClass: 'bg-neutral-100 dark:bg-neutral-800',
});

const route = useRoute();

let id = route.params?.id;
if (id && id.includes('-nft1')) {
  id = `nft1${id.split('-nft1').pop()}`;
}
const { data: nft } = await useFetch<any>(`${getApiRoot()}/nfts/${id}`);

const metadata = computed(() => nft.value?.data?.metadata_json || nft.value?.data?.metadata);
const name = computed(() => metadata.value?.name || (nft.value ? 'Unnamed' : 'Unknown NFT'));

const meta = [
  { property: 'og:title', content: `${name.value} ${BASE_TITLE}` },
  { property: 'og:image', content: nft.value?.data?.thumbnail_uri || nft.value?.thumbnail_uri },
  { name: 'twitter:image', content: nft.value?.data?.thumbnail_uri || nft.value?.thumbnail_uri },
  { name: 'twitter:title', content: `${name.value} ${BASE_TITLE}` },
  { name: 'twitter:card', content: 'summary_large_image' },
  {
    name: 'og:description',
    content: metadata.value?.description || 'Explore this and other NFTs on MintGarden.',
  },
  {
    name: 'twitter:description',
    content: metadata.value?.description || 'Explore this and other NFTs on MintGarden.',
  },
];

useHead({
  title: `${name.value} ${BASE_TITLE}`,
  meta,
});

const associatedMedia = computed(() => {
  return nft.value?.data?.metadata_json?.data?.associatedMedia || [];
});

const webContent = computed(() => {
  return nft.value?.data?.metadata_json?.data?.['@type'] === 'WebContent'
    ? nft.value.data.metadata_json.data
    : undefined;
});

const activeMedia = ref<any>(undefined);
const setActiveMedia = (media: any | undefined) => {
  activeMedia.value = media;
};

const dataType = computed(() => nft.value?.data?.data_type);

const getMediaUrl = (media: any) => {
  if (media?.url && Array.isArray(media.url)) {
    return media.url[0];
  }
  return media?.url;
};

const isDynamic = computed(() => {
  return nft.value?.collection?.id === 'col1lvle5tj7qje0hczn0zjkgy7e7juvh9h5phddt6tcqpq6znt7jpms0dywrw';
});

const previewUri = computed(() => {
  if (activeMedia.value) {
    return getMediaUrl(activeMedia.value);
  }
  const firstDataUri = nft.value?.data?.data_uris?.[0];
  if (firstDataUri && (dataType.value === 3 || dataType.value === 4 || isDynamic.value)) {
    return firstDataUri;
  }
  return nft.value?.data?.preview_uri;
});

const originalUrl = computed(() => (nft.value ? `${getApiRoot()}/nfts/${id}/original` : ''));
const metadataUrl = computed(() => (nft.value ? `${getApiRoot()}/nfts/${id}/metadata` : ''));
const licenseUrl = computed(() => (nft.value ? `${getApiRoot()}/nfts/${id}/license` : ''));

const copied = ref(false);
const copyToClipboard = (input: string) => {
  navigator.clipboard.writeText(input);
  copied.value = true;
  setTimeout(() => (copied.value = false), 1000);
};

const showSensitive = ref(false);
const isSensitiveContent = (nft) =>
  nft.collection?.sensitive_content ||
  nft.data?.metadata_json?.sensitive_content === true ||
  nft.data?.metadata_json?.sensitive_content === 'true';

const isZoomedIn = ref(false);
const expandAttributes = ref(false);

const getNumItems = (attributeFrequencyCounts: any) => {
  return Object.values<number>(attributeFrequencyCounts['meta_trait:trait_count']).reduce((acc, val) => acc + val, 0);
};
const getAttributeFrequency = (attributeFrequencyCounts: any, attribute: any) => {
  const traitType = (attribute.trait_type || attribute.name)?.toLowerCase();
  const value = String(attribute.value).toLowerCase();
  return attributeFrequencyCounts[traitType]?.[value];
};

const getAttributeLink = (attribute: any) => {
  const urls =
    typeof attribute?.value === 'string' || attribute?.value instanceof String
      ? Autolinker.parse(attribute.value, { urls: true, phone: false, email: false }).filter(
          (url) => url.type === 'url'
        )
      : [];

  if (urls.length > 0) {
    return { href: urls[0].getUrl(), target: '_blank' };
  } else if (nft.value.collection) {
    return {
      href: `/collections/${nft.value.collection.id}?filter=${attribute.name || attribute.trait_type}&filter_value=${
        attribute.value
      }`,
      target: '_self',
    };
  }
  return undefined;
};

const gpuEarningsUrl = computed(() => {
  const uuid =
    nft.value?.creator?.id === 'd3482ac8ce5745c9985b55c0bbf4855a7c081403f2749936438259cc24fa736a'
      ? metadata.value.attributes?.find((a: any) => a.trait_type === 'gpu_uuid')?.value
      : undefined;
  return uuid ? `https://app.silicon.net/${uuid}` : '';
});
</script>

<template>
  <div>
    <div class="pt-6 dark:text-white lg:pt-0">
      <Error404 v-if="!nft" />
      <!-- Product -->
      <div v-else class="flex flex-col items-center">
        <div class="flex h-[50vh] w-full justify-center">
          <div
            :class="
              isZoomedIn && activeMedia?.['@type'] !== '3DModel'
                ? 'fixed inset-0 z-50 flex h-auto max-h-full w-auto max-w-full items-center justify-center bg-zinc-800'
                : 'relative flex w-full items-center justify-center overflow-hidden sm:py-12 lg:rounded-xl lg:p-16'
            "
          >
            <BanIcon v-if="nft.creator?.verification_state === 2" class="h-1/2 w-1/2 text-red-600" />
            <BanIcon
              v-else-if="nft.is_blocked || nft.collection?.blocked_content"
              class="h-1/2 w-1/2 text-neutral-600 dark:text-neutral-300"
            />
            <div v-else-if="webContent && (!isSensitiveContent(nft) || showSensitive)" class="h-full w-full">
              <iframe
                class="mx-auto aspect-1 h-full max-w-full"
                :sandbox="`allow-scripts${
                  nft.creator.id === 'f0bafce98004ac7e3571bba27ae2f0106d164f0abc765e4bf2477197993e65c6'
                    ? ' allow-same-origin'
                    : ''
                }`"
                :src="webContent.url"
              ></iframe>
            </div>
            <LazyModelViewer3D
              v-else-if="activeMedia?.['@type'] === '3DModel'"
              :src="getMediaUrl(activeMedia)"
              :poster="previewUri"
            />
            <video
              v-else-if="activeMedia?.['@type'] === 'VideoObject' || dataType === 3"
              autoplay
              loop
              controls
              muted
              :class="[isSensitiveContent(nft) && !showSensitive ? 'blur-xl' : '']"
              class="max-h-full"
              :poster="nft?.data?.thumbnail_uri"
            >
              <source :src="previewUri" type="video/mp4" />
            </video>
            <div v-else-if="dataType === 4" class="flex flex-col items-center">
              <MusicNoteIcon class="h-24 w-24 text-neutral-400 dark:text-neutral-500 md:h-36 md:w-36" />
              <audio controls>
                <source :src="previewUri" type="audio/mpeg" />
              </audio>
            </div>
            <img
              v-else-if="previewUri"
              id="nft-image"
              :src="isZoomedIn ? getMediaUrl(activeMedia) || originalUrl : previewUri"
              class="max-h-full object-contain drop-shadow-[0_4px_20px_rgba(0,0,0,0.25)]"
              :class="[
                isSensitiveContent(nft) && !showSensitive ? 'blur-xl' : '',
                isZoomedIn ? 'relative cursor-zoom-out' : 'cursor-zoom-in lg:rounded-xl',
              ]"
              @click="isZoomedIn = !isZoomedIn"
            />
            <QuestionMarkCircleIcon
              v-else
              class="h-1/2 w-1/2 text-neutral-400 transition duration-150 ease-in-out dark:text-neutral-500"
            />
            <div v-if="associatedMedia" class="absolute bottom-4 right-4 flex gap-4">
              <button
                v-for="media in associatedMedia.filter((m) => m !== activeMedia)"
                :key="media.name"
                type="button"
                class="rounded-full bg-neutral-900 p-2 text-white opacity-70 shadow-sm hover:bg-neutral-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-neutral-50 dark:text-black dark:hover:bg-neutral-100"
                @click="setActiveMedia(media)"
              >
                <CubeIcon v-if="isZoomedIn" class="h-6 w-6 lg:h-8 lg:w-8" aria-hidden="true" />
                <CubeTransparentIcon
                  v-else-if="media['@type'] === '3DModel'"
                  class="h-6 w-6 lg:h-8 lg:w-8"
                  aria-hidden="true"
                />
                <VideoCameraIcon
                  v-else-if="media['@type'] === 'VideoObject'"
                  class="h-6 w-6 lg:h-8 lg:w-8"
                  aria-hidden="true"
                />
                <PhotographIcon v-else class="h-6 w-6 lg:h-8 lg:w-8" aria-hidden="true" />
              </button>
              <button
                v-if="activeMedia"
                type="button"
                class="rounded-full bg-neutral-900 p-2 text-white opacity-70 shadow-sm hover:bg-neutral-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-neutral-50 dark:text-black dark:hover:bg-neutral-100"
                @click="setActiveMedia(undefined)"
              >
                <XIcon class="h-6 w-6 lg:h-8 lg:w-8" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="nft.is_blocked || nft.collection?.blocked_content"
          class="h-12 w-full bg-neutral-600 dark:bg-neutral-300"
        >
          <div class="flex h-full items-center justify-center">
            <span class="font-semibold text-white dark:text-black"
              >This NFT has been removed from display and trading.{{
                nft.collection?.blocked_content_reason ? ` Reason: ${nft.collection.blocked_content_reason}` : ''
              }}</span
            >
          </div>
        </div>
        <div v-else-if="isSensitiveContent(nft)" class="h-12 w-full bg-neutral-600 dark:bg-neutral-300">
          <div class="flex h-full items-center justify-center">
            <a href="#" class="font-semibold text-white dark:text-black" @click.prevent="showSensitive = true"
              >This NFT is marked as sensitive content. Click here to display it.</a
            >
          </div>
        </div>
        <div
          v-if="nft?.creator?.verification_state === 2 || nft.data.integrity_state === 2"
          :class="nft?.creator?.verification_state === 2 ? 'bg-red-600' : 'bg-orange-600'"
          class="h-12 w-full"
        >
          <div class="flex h-full items-center justify-center">
            <span v-if="nft?.creator?.verification_state === 2" class="font-semibold text-white dark:text-black"
              >This NFT has been blocked in the
              <NuxtLink to="https://github.com/dexie-space/blocklist" target="_blank" class="underline"
                >community blocklist</NuxtLink
              >.</span
            >
            <span v-else class="font-semibold text-white dark:text-black"
              >This content does not match the data hash stored in the NFT.</span
            >
          </div>
        </div>
        <div class="w-full bg-white dark:bg-black">
          <div
            class="mx-auto w-full max-w-3xl px-4 sm:px-8 xl:grid xl:max-w-screen-2xl xl:grid-cols-8 xl:gap-x-32 xl:gap-y-10 xl:px-16"
          >
            <div
              :class="[isDynamic ? 'mt-4' : 'mt-14 sm:mt-16 xl:mt-12']"
              class="w-full xl:col-span-4 xl:row-span-1 xl:row-end-1 xl:max-w-none"
            >
              <div v-if="isDynamic" class="mb-2 flex items-center">
                <div class="group relative">
                  <div
                    class="flex items-center gap-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-3 py-1.5 text-sm font-semibold text-white shadow-lg"
                  >
                    <SparklesIcon class="h-4 w-4" />
                    Dynamic
                  </div>
                  <div
                    class="absolute left-0 top-full z-10 mt-2 hidden w-64 rounded-lg bg-black p-3 text-xs text-white shadow-lg group-hover:block dark:bg-neutral-900"
                  >
                    <div class="mb-1 font-semibold">Dynamic NFT</div>
                    This NFT has interactive or changing content that can evolve over time based on various conditions
                    or user interactions.
                  </div>
                </div>
              </div>
              <div>
                <h1 class="flex items-end gap-4 text-4xl font-semibold text-black dark:text-white xl:text-5xl">
                  {{ name }}
                  <span
                    v-if="nft.data.edition_number && nft.data.edition_total !== 1"
                    class="whitespace-nowrap text-2xl font-medium"
                    >{{ nft.data.edition_number }} / {{ nft.data.edition_total || '∞' }}</span
                  >
                </h1>
                <div class="p-1">
                  <div
                    class="group flex cursor-pointer items-center overflow-hidden"
                    @click="copyToClipboard(nft.encoded_id)"
                  >
                    <span class="font-mono text-sm tracking-tight text-neutral-500 dark:text-neutral-400 xl:text-base"
                      ><span class="font-semibold text-emerald-600">nft1</span
                      >{{ nft.encoded_id.replace('nft1', '') }}</span
                    >
                    <div class="flex hidden group-hover:inline">
                      <ClipboardCheckIcon v-if="copied" class="h-5 w-5 text-emerald-600 xl:h-6 xl:w-6" />
                      <ClipboardCopyIcon
                        v-else
                        class="ml-[1px] h-5 w-5 text-neutral-500 dark:text-neutral-400 xl:h-6 xl:w-6"
                      />
                    </div>
                  </div>
                </div>
                <p
                  v-if="metadata?.description"
                  class="mt-6 max-h-[15rem] overflow-auto whitespace-pre-wrap text-neutral-900 dark:text-neutral-50"
                >
                  {{ metadata.description }}
                </p>
                <div v-if="gpuEarningsUrl" class="mt-6 flex">
                  <NuxtLink
                    :to="gpuEarningsUrl"
                    target="_blank"
                    class="betterhover:hover:shadow-card-hover flex items-center justify-center gap-2 rounded-full border border-transparent bg-black px-6 py-3 font-semibold text-white transition duration-100 ease-in-out dark:bg-neutral-100 dark:text-black betterhover:hover:-translate-y-0.5"
                  >
                    {{ $t('details.seeEarningsHistory') }}
                    <ExternalLinkIcon class="h-4 w-4" />
                  </NuxtLink>
                </div>
                <div class="mt-8 grid grid-rows-1 gap-y-6 sm:grid-cols-3">
                  <div class="cols-span-1 flex flex-col gap-2">
                    <span class="text-sm font-bold text-neutral-700 dark:text-neutral-200">{{
                      $t('details.creator')
                    }}</span>
                    <ProfileHandle v-if="nft.creator" :profile="nft.creator" avatar-dimension="2rem" />
                    <AddressHandle v-else :address="nft.creator_address" avatar-dimension="2rem" />
                  </div>
                  <div class="cols-span-1 flex flex-col gap-2 overflow-hidden">
                    <span class="text-sm font-bold text-neutral-700 dark:text-neutral-200">{{
                      $t('details.owner')
                    }}</span>
                    <ProfileHandle v-if="nft.owner" :profile="nft.owner" avatar-dimension="2rem" />
                    <div
                      v-else-if="
                        !nft.owner_address ||
                        nft.owner_address.id === '000000000000000000000000000000000000000000000000000000000000dead'
                      "
                      class="flex items-center gap-1"
                    >
                      <span class="inline flex h-8 w-8 items-center justify-center rounded-full bg-red-600">
                        <FireIcon class="h-5 w-5 text-white dark:text-black"></FireIcon>
                      </span>
                      <span class="text-neutral-700 dark:text-neutral-200">{{ $t('details.burned') }}</span>
                    </div>
                    <AddressHandle v-else :address="nft.owner_address" avatar-dimension="2rem" />
                  </div>
                  <div v-if="nft.collection" class="cols-span-1 flex flex-col gap-2">
                    <span class="text-sm font-bold text-neutral-700 dark:text-neutral-200">{{
                      $t('details.collection')
                    }}</span>
                    <CollectionHandle :collection="nft.collection" avatar-dimension="2rem" />
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="!nft.is_blocked && !nft.collection?.blocked_content && nft.creator?.verification_state !== 2"
              class="mt-14 w-full sm:mt-16 xl:col-span-4 xl:row-span-1 xl:row-end-1 xl:mt-12 xl:max-w-none"
            >
              <DetailsOfferPanel :nft="nft" />
            </div>

            <div class="mt-14 w-full sm:mt-16 xl:col-span-4 xl:row-span-1 xl:row-start-1 xl:mt-0 xl:max-w-none">
              <div v-if="metadata?.attributes && metadata.attributes.length > 0" class="mb-8">
                <h3 class="text-2xl font-bold">{{ $t('details.attributes') }}</h3>
                <div class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  <NuxtLink
                    v-for="(attribute, index) in metadata.attributes"
                    :key="attribute.name || attribute.trait_type"
                    :to="getAttributeLink(attribute)?.href || null"
                    :target="getAttributeLink(attribute)?.target"
                    :class="expandAttributes || index < 4 ? '' : 'hidden'"
                    class="lg:block"
                  >
                    <div
                      class="shadow-attribute relative block h-full rounded-lg p-4 transition duration-100 ease-in-out hover:ring hover:ring-black dark:bg-neutral-800 dark:hover:ring-white hover:dark:ring-neutral-300"
                    >
                      <p class="break-words text-sm font-semibold text-neutral-500 dark:text-neutral-400">
                        {{ attribute.name || attribute.trait_type }}
                      </p>
                      <p class="break-words text-base font-semibold text-neutral-900 dark:text-neutral-50">
                        {{ attribute.value || '&nbsp;'
                        }}<span
                          v-if="attribute.min_value != null && attribute.max_value != null"
                          class="font-medium text-neutral-400 dark:text-neutral-500"
                        >
                          of {{ attribute.max_value }}
                        </span>
                      </p>
                      <p
                        v-if="nft.collection?.attributes_frequency_counts"
                        class="break-words text-xs font-semibold text-neutral-500 dark:text-neutral-400"
                      >
                        {{
                          Math.round(
                            ((getAttributeFrequency(nft.collection.attributes_frequency_counts, attribute) * 100) /
                              getNumItems(nft.collection.attributes_frequency_counts)) *
                              10000
                          ) / 10000
                        }}%
                      </p>
                      <div
                        v-if="attribute.min_value != null && attribute.max_value != null"
                        class="relative mt-1 flex-1"
                      >
                        <div
                          class="h-3 rounded-full border border-neutral-200 bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800"
                        />
                        <div
                          class="absolute inset-y-0 rounded-full border border-emerald-500 bg-emerald-500"
                          :style="{
                            width: `calc(${attribute.value - attribute.min_value} / ${
                              attribute.max_value - attribute.min_value
                            } * 100%)`,
                            maxWidth: '100%',
                          }"
                        />
                      </div>
                    </div>
                  </NuxtLink>
                </div>
                <a
                  v-if="metadata?.attributes && metadata.attributes.length > 4"
                  href="#"
                  class="mt-4 inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:text-neutral-50 lg:hidden"
                  @click.prevent="expandAttributes = !expandAttributes"
                >
                  <ChevronUpIcon v-if="expandAttributes" class="h-4 w-4" />
                  <ChevronDownIcon v-else class="h-4 w-4" />
                  {{ expandAttributes ? 'Collapse' : 'Show all' }}
                </a>
              </div>
              <div class="grid gap-8 sm:grid-cols-2">
                <div>
                  <h3 class="text-2xl font-bold">{{ $t('details.title') }}</h3>
                  <div class="mt-4 flex flex-col gap-4">
                    <div class="sm:col-span-1">
                      <span class="flex items-center gap-1 text-sm font-medium text-neutral-700 dark:text-neutral-200"
                        ><SparklesIcon class="h-6 w-6" />{{ $t('details.mintHeight') }}:
                        {{ nft.launcher_coin.spent_block_index || 'Unminted' }}</span
                      >
                    </div>
                    <div
                      v-if="nft.data && nft.data.edition_number != null && nft.data.edition_total !== 1"
                      class="sm:col-span-1"
                    >
                      <span class="flex items-center gap-1 text-sm font-medium text-neutral-700 dark:text-neutral-200"
                        ><CollectionIcon class="h-6 w-6" />{{ $t('details.edition') }}: {{ nft.data.edition_number }} /
                        {{ nft.data.edition_total || '∞' }}</span
                      >
                    </div>
                    <div
                      v-if="metadata && metadata.series_number != null && metadata.series_total !== 1"
                      class="sm:col-span-1"
                    >
                      <span class="flex items-center gap-1 text-sm font-medium text-neutral-700 dark:text-neutral-200"
                        ><CollectionIcon class="h-6 w-6" />{{ $t('details.series') }}: {{ metadata.series_number }} /
                        {{ metadata.series_total || '∞' }}</span
                      >
                    </div>
                    <div class="sm:col-span-1">
                      <span class="flex items-center gap-1 text-sm font-medium text-neutral-700 dark:text-neutral-200"
                        ><CashIcon class="h-6 w-6" />{{ $t('details.perpetualRoyalties') }}:
                        {{ nft.royalty_percentage / 100 }}%</span
                      >
                    </div>
                    <div v-if="nft.openrarity_rank" class="sm:col-span-1">
                      <NuxtLink
                        to="https://docs.mintgarden.io/mintgarden/rarity"
                        target="_blank"
                        class="flex items-center gap-1 text-sm font-medium text-neutral-700 hover:text-neutral-900 dark:text-neutral-200 dark:text-neutral-50"
                      >
                        <ChartBarIcon class="h-6 w-6" />
                        {{ $t('details.openRarityRank') }}: {{ nft.openrarity_rank }}
                      </NuxtLink>
                    </div>
                    <div class="sm:col-span-1">
                      <span class="flex items-center gap-1 text-sm font-medium text-neutral-700 dark:text-neutral-200"
                        ><HashtagIcon class="h-6 w-6" />Owner Address:
                        <AddressHandle avatar-dimension="" :address="nft.owner_address" />...</span
                      >
                    </div>
                    <div v-if="nft.data" class="sm:col-span-1">
                      <NuxtLink
                        class="flex items-center gap-1 text-sm font-medium text-neutral-700 dark:text-neutral-200"
                        :class="
                          nft.data.license_integrity_state !== 0 ? 'hover:text-neutral-900 dark:text-neutral-50' : ''
                        "
                        :to="nft.data.license_integrity_state !== 0 ? licenseUrl : undefined"
                        target="_blank"
                      >
                        <DocumentTextIcon v-if="nft.data.license_integrity_state === 1" class="h-6 w-6" />
                        <ExclamationIcon v-else class="h-6 w-6 text-orange-600" />
                        {{
                          nft.data.license_integrity_state === 1
                            ? $t('details.license')
                            : nft.data.license_integrity_state === 2
                            ? $t('details.licenseMismatch')
                            : $t('details.noLicense')
                        }}
                      </NuxtLink>
                    </div>
                    <div class="mt-8 sm:col-span-1">
                      <NuxtLink
                        class="flex items-center gap-1 text-sm font-medium text-neutral-700 hover:text-neutral-900 dark:text-neutral-200 dark:text-neutral-50"
                        :to="`https://www.spacescan.io/${
                          getApiRoot().indexOf('testnet') !== -1 ? 'txch10' : 'xch'
                        }/nft/${nft.encoded_id}`"
                        target="_blank"
                      >
                        <img class="h-6 w-6 rounded-full border" src="/spacescan.ico" />{{
                          $t('details.inspectOnSpacescan')
                        }}
                      </NuxtLink>
                    </div>
                    <div v-if="nft.data && !nft.collection?.blocked_content" class="sm:col-span-1">
                      <NuxtLink
                        class="flex items-center gap-1 text-sm font-medium text-neutral-700 hover:text-neutral-900 dark:text-neutral-200 dark:text-neutral-50"
                        :to="originalUrl"
                        target="_blank"
                      >
                        <ExternalLinkIcon class="h-6 w-6" />
                        {{ $t('details.openOriginalDataURI') }}
                      </NuxtLink>
                    </div>
                    <div v-if="nft.data" class="sm:col-span-1">
                      <NuxtLink
                        class="flex items-center gap-1 text-sm font-medium text-neutral-700 hover:text-neutral-900 dark:text-neutral-200 dark:text-neutral-50"
                        :to="metadataUrl"
                        target="_blank"
                      >
                        <ExternalLinkIcon class="h-6 w-6" />
                        {{ $t('details.openOriginalMetadataURI') }}
                      </NuxtLink>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-14 w-full sm:mt-16 xl:col-span-4 xl:mt-0 xl:max-w-none">
              <div class="flex grid-cols-2 flex-col gap-4">
                <h3 class="text-2xl font-bold">{{ $t('details.provenance') }}</h3>
                <DetailsProvenance :nft="nft" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
